import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { SetupConsumerPortalRequest, SetupConsumerPortalResponse, SetupConsumerPortalApiResponse } from '../models/onboarding.model';
import { ConsumerPortalApiService } from './consumer-portal-api.service';

/** Credentials resolved for an onboarding request, plus which ones still need prompting. */
export interface OnboardCredentials {
    cognitoToken: string;
    relationshipId: string;
    needsCognitoToken: boolean;
    needsRelationshipId: boolean;
}

const orgNotProvisionedMessage = 'This organization is not fully provisioned in RMS (missing database ID) and cannot be onboarded to Consumer Portal.';
const rmsCommsMessage = 'RMS API communication failed. Please try again or contact DevOps.';
const genericOnboardError = 'Consumer Portal setup unsuccessful. Please contact the Dev team for assistance.';
const alreadyOnboardedMessage = 'Organization is already onboarded to Consumer Portal';
const invalidEmailMessage = 'Access restricted to @pointofrental.com email addresses';
const invalidTokenMessage = 'Authentication token is invalid or expired';

/* eslint-disable @typescript-eslint/naming-convention */
const tokenMessages: Record<string, string> = {
    ALREADY_ONBOARDED: alreadyOnboardedMessage,
    INVALID_EMAIL_DOMAIN: invalidEmailMessage,
    INVALID_TOKEN: invalidTokenMessage,
    INVALID_ORG_TYPE: 'Organization type is not supported for Consumer Portal',
    ORG_NOT_PROVISIONED: orgNotProvisionedMessage,
    FAILED_TO_GET_RMS_ORGANIZATION: rmsCommsMessage,
    FAILED_TO_GET_RMS_LICENSES: rmsCommsMessage,
    FAILED_TO_CREATE_RMS_LICENSE: rmsCommsMessage,
    FAILED_TO_GET_RMS_API_KEYS: rmsCommsMessage,
    FAILED_TO_CREATE_RMS_API_KEY: rmsCommsMessage,
    FAILED_TO_GET_RMS_ORG_CONFIG: rmsCommsMessage,
    ONBOARD_FAILED: genericOnboardError
};
/* eslint-enable @typescript-eslint/naming-convention */

@Injectable({
    providedIn: 'root'
})
export class AdminOnboardingService {
    private readonly http = inject(HttpClient);
    private readonly consumerPortalApi = inject(ConsumerPortalApiService);

    /**
     * Trigger Consumer Portal onboarding for organization
     *
     * Uses raw HttpClient (not ConsumerPortalApiService.send()) to avoid
     * adding expired/invalid Authorization headers. The onboarding API
     * authenticates via cognitoToken in the request body.
     *
     * @param email - User email (@pointofrental.com)
     * @param orgId - Organization ID
     * @param relationshipId - Environment ID (39500 prod, 62701 dev)
     * @param cognitoToken - Cognito JWT token for RMS API access
     * @returns Observable with onboarding response
     */
    onboardOrganization(email: string, orgId: string, relationshipId: string, cognitoToken: string): Observable<SetupConsumerPortalResponse> {
        const request: SetupConsumerPortalRequest = {
            email,
            orgId,
            relationshipId,
            cognitoToken
        };

        const url = this.consumerPortalApi.apiRoute + '/apikey/onboard/setup';

        return this.http.post<SetupConsumerPortalApiResponse>(url, request).pipe(
            map(response => response.Records),
            catchError((error: HttpErrorResponse) => {
                return throwError(() => error);
            })
        );
    }

    /**
     * Single source of truth for credential resolution: embed config first,
     * then localStorage. Returns needs* flags so the UI can prompt for gaps.
     */
    resolveCredentials(config?: { cognitoToken?: string; relationshipId?: string }): OnboardCredentials {
        const cognitoToken = config?.cognitoToken || this.readCognitoIdTokenFromLocalStorage();
        const relationshipId = config?.relationshipId || this.readRelationshipIdFromLocalStorage();
        return {
            cognitoToken,
            relationshipId,
            needsCognitoToken: !cognitoToken,
            needsRelationshipId: !relationshipId
        };
    }

    /** Amplify writes the idToken under CognitoIdentityServiceProvider.{clientId}.{userId}.idToken. */
    readCognitoIdTokenFromLocalStorage(): string {
        try {
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key?.startsWith('CognitoIdentityServiceProvider') && key.endsWith('.idToken')) {
                    return localStorage.getItem(key) || '';
                }
            }
        } catch {
            // localStorage unavailable in some embedded contexts
        }
        return '';
    }

    /** GAdmin (NGRX localStorageSync) persists relationshipId at auth.relationship.RelationshipId. */
    readRelationshipIdFromLocalStorage(): string {
        try {
            const authData = localStorage.getItem('auth');
            if (authData) {
                /* eslint-disable-next-line @typescript-eslint/naming-convention */
                const parsed = JSON.parse(authData) as { relationship?: { RelationshipId?: number | string }; apiUser?: { RelationshipId?: number | string } };
                const relationshipId = parsed?.relationship?.RelationshipId ?? parsed?.apiUser?.RelationshipId;
                if (relationshipId) {
                    return relationshipId.toString();
                }
            }
        } catch {
            // not valid JSON or localStorage unavailable
        }
        return localStorage.getItem('por_relationship_id') || '';
    }

    /** Persist only relationshipId (stable across sessions); never cache the short-lived cognitoToken. */
    persistRelationshipId(relationshipId: string): void {
        try {
            localStorage.setItem('por_relationship_id', relationshipId);
        } catch {
            // localStorage unavailable in some embedded contexts
        }
    }

    /** Token-first (precise), then HTTP-status fallback so both backend taxonomies are covered. */
    mapErrorToUserMessage(error: HttpErrorResponse): string {
        const token = error?.error?.token as string | undefined;
        if (token && tokenMessages[token]) {
            return tokenMessages[token];
        }

        const backendMessage = error?.error?.message as string | undefined;
        const statusMessage = this.messageForStatus(error?.status, backendMessage);
        if (statusMessage) {
            return statusMessage;
        }

        return backendMessage || genericOnboardError;
    }

    /** AC wording — replaces the button after a successful onboard. */
    buildSuccessMessage(orgId: string, vanityUrl: string): string {
        const url = vanityUrl || `${orgId}.myportal.rent`;
        return `It may take up to 3 to 5 minutes for the setup process to complete. Open the Consumer Portal Admin Panel or route to ${url} after 5 minutes.`;
    }

    private messageForStatus(status: number | undefined, backendMessage: string | undefined): string {
        switch (status) {
            case 422:
                return orgNotProvisionedMessage;
            case 409:
                return alreadyOnboardedMessage;
            case 403:
                return invalidEmailMessage;
            case 401:
                return invalidTokenMessage;
            case 400:
                return backendMessage || 'Invalid request. Please check your credentials.';
            default:
                return '';
        }
    }
}
